import { getAdminStatsApi } from '@/lib/api/usersApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Building2, ExternalLink, LayoutDashboard, Shield, Trophy, Users } from 'lucide-react';

const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined) ?? 'http://localhost:5231';

function formatCents(cents: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function AdminQuickActions() {
  return (
    <div className='mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
      <a
        href={`${ADMIN_URL}/admin/dashboard`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-violet-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 transition group-hover:bg-violet-100'>
          <LayoutDashboard className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Administration</p>
          <p className='text-xs text-slate-500'>Tableau de bord plateforme</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-violet-500' />
      </a>

      <a
        href={`${ADMIN_URL}/admin/users`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-blue-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-100'>
          <Users className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Utilisateurs</p>
          <p className='text-xs text-slate-500'>Gérer les comptes</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-blue-500' />
      </a>

      <a
        href={`${ADMIN_URL}/admin/organizations`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-emerald-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition group-hover:bg-emerald-100'>
          <Building2 className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Organisations</p>
          <p className='text-xs text-slate-500'>Organisateurs et clubs</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-emerald-500' />
      </a>
    </div>
  );
}

export function AdminPlatformStats() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const { data: stats, isLoading } = useQuery({
    queryKey: queryKeys.adminStats(),
    queryFn: getAdminStatsApi,
    enabled: isAuthenticated,
  });

  if (isLoading) {
    return (
      <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className='h-24 animate-pulse rounded-2xl bg-slate-100' />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      icon: Users,
      label: 'Utilisateurs',
      value: stats.users.total,
      sub: `+${stats.users.newThisMonth} ce mois`,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      icon: Building2,
      label: 'Organisations',
      value: stats.organizations.total,
      sub: `+${stats.organizations.newThisMonth} ce mois`,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
    {
      icon: Shield,
      label: 'Clubs',
      value: stats.clubs.total,
      sub: `${stats.clubs.active} actifs`,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      icon: Trophy,
      label: 'Concours',
      value: stats.competitions.total,
      sub: `${stats.competitions.upcoming} à venir`,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      icon: Users,
      label: 'Inscriptions',
      value: stats.registrations.total,
      sub: `+${stats.registrations.thisMonth} ce mois`,
      color: 'text-cyan-600',
      bg: 'bg-cyan-50',
    },
    {
      icon: BarChart3,
      label: 'CA commission',
      value: formatCents(stats.revenue.thisYearFeeCents),
      sub: `${formatCents(stats.revenue.thisMonthFeeCents)} ce mois`,
      color: 'text-violet-600',
      bg: 'bg-violet-50',
    },
  ];

  return (
    <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3'>
      {statCards.map(card => (
        <div key={card.label} className='rounded-2xl bg-white border border-slate-100 shadow-sm p-5'>
          <div className='flex items-center gap-2 mb-2'>
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${card.bg}`}>
              <card.icon className={`h-4 w-4 ${card.color}`} />
            </div>
            <p className='text-xs font-medium text-slate-500'>{card.label}</p>
          </div>
          <p className={`text-xl font-extrabold ${card.color}`}>{card.value}</p>
          <p className='mt-0.5 text-xs text-slate-400'>{card.sub}</p>
        </div>
      ))}
    </div>
  );
}
